import type { ButtonHTMLAttributes } from 'react'
import type { ActionId } from '@/annotations'
import { Icon } from './Icon'
import type { IconName } from './icons/iconData'
import { cssVar } from '@/styles/tokens'

/**
 * `button.iconbtn` → `icon-button` (process/08-COMPONENT-CATALOGUE.md: "Icon only, no label").
 * Prototype_v2.html's own `.iconbtn` rule: 26×26px, `border:0`, transparent background, hover
 * background `--row`, `color:var(--tx2)`, `border-radius:3px`. 26px has no token (same class of
 * gap `Button.tsx`'s own `.full` variant already documents); radius → `r-1`, idle color → `tx2`,
 * hover background → `row`, both from the closed vocabulary.
 *
 * `label` is required, not optional — this control carries no visible text by construction, so
 * `aria-label`/`title` are the only place its purpose is stated at all.
 */
export function IconButton({
  'data-act': dataAct,
  icon,
  label,
  ...rest
}: {
  'data-act': ActionId | undefined
  icon: IconName
  label: string
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'title'>) {
  return (
    <button
      data-cmp="icon-button"
      data-act={dataAct}
      aria-label={label}
      title={label}
      style={{ width: '26px', height: '26px', borderRadius: cssVar('r-1'), color: cssVar('tx2') }}
      className="inline-grid place-items-center border-0 bg-transparent cursor-pointer hover:bg-row disabled:cursor-not-allowed disabled:opacity-50"
      {...rest}
    >
      <Icon name={icon} size="s" />
    </button>
  )
}
